import { Stack, Typography } from '@sureco/design-system'
import {
  Button,
  Modal,
  notification,
  Select,
  Spin,
  Tag,
  Tooltip,
  Upload,
} from '@surecompanies/core_components'
import ContentSection from 'layouts/ContentSection/ContentSection'
import React, { useEffect, useRef, useState } from 'react'

import {
  CheckCircleOutlined,
  CopyOutlined,
  SettingOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useRouterContext } from 'components/RouterContext'
import { benchmarkPlansPath } from 'enrollment-platform/pages/BenchmarkPlans/paths'
import Grid from 'components/Grid/Grid'
import {
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import GridNoResultsOverlay from 'components/GridNoResultsOverlay'
import epsClientV1 from 'enrollment-platform/api/eps-v1.client'
import dayjs, { convertToLocalFromUTCISODate } from 'helpers/datetime'
import { downloadFile } from 'helpers/download-file'
import ContributionProcessingSection from './components/ContributionProcessingSection'
import ContributionReportSection from './components/ContributionReport/ContributionReportSection'
import CurrentActiveModel from './components/CurrentActiveModel'
import SearchableByExample from './components/SearchableByExample/SearchableByExample'
import useContributions from './hooks/useContributions'
import useContributionsJobs from './hooks/useContributionsJobs'
import {
  ContributionJobHistoryItem,
  ContributionJobHistoryStatus,
} from './types/types'
import { getBenchmarkLabel } from './utils'

function getHistoryStatusLabel(
  status: ContributionJobHistoryStatus,
  errorMessage?: string | null
) {
  switch (status) {
    case 'completed':
      return <Tag color="#98ce24">Completed</Tag>
    case 'failed': {
      // Surface the failure message on hover so it stays "captured on the
      // table" without a dedicated (wide) logs column.
      const tag = (
        <Tag
          color="#ff4d4f"
          style={errorMessage ? { cursor: 'help' } : undefined}
        >
          Failed
        </Tag>
      )
      return errorMessage ? (
        <Tooltip
          overlayStyle={{ maxWidth: 480 }}
          title={<span style={{ whiteSpace: 'pre-wrap' }}>{errorMessage}</span>}
        >
          {tag}
        </Tooltip>
      ) : (
        tag
      )
    }
    case 'cancelled':
      return <Tag color="#f5222d">Cancelled</Tag>
    case 'queued':
    case 'processing':
      return (
        <Tag color="#1890ff">{status === 'queued' ? 'Queued' : 'Active'}</Tag>
      )
    default:
      return <Tag color="#f5222d">Unknown Status: {status}</Tag>
  }
}

export default function ContributionTab({ id: customerId }: { id: string }) {
  const { router } = useRouterContext()
  const {
    isLoading,
    yearOptions,
    data,
    selectedYear,
    onUpload,
    onChangeYear,
    selectedContributionRule,
    setSelectedContributionRule,
    isUploading,
    benchmarkSnapshots,
    benchmarkSnapshotOptions,
    selectedBenchmarkSnapshot,
    onChangeBenchmarkSnapshot,
    isBenchmarkSnapshotsLoading,
    refetchContributions,
  } = useContributions({
    id: customerId,
  })

  const {
    isProcessing,
    jobResponse,
    setManuallyTriggeredJobId,
    getAllCustomerContributionJobs,
    applyContributionModel,
  } = useContributionsJobs(customerId, async () => {
    // Callback executed when job completes
    await Promise.all([refetchContributions(), refetchContributionJobs()])
  })

  const {
    data: contributionJobs,
    isLoading: isLoadingContributionJobs,
    refetch: refetchContributionJobs,
  } = getAllCustomerContributionJobs()

  // The history table polls while any apply is queued/processing (incl. async
  // large-model imports). When the last active job finishes, refresh the
  // contributions so the active-model card and the model dropdown reflect the
  // newly activated model without a manual page reload.
  const hadActiveContributionJobsRef = useRef(false)
  // Reset the tracker when switching customers so the previous customer's
  // active-job state can't trigger a spurious refetch for the new one. Declared
  // before the effect below so it runs first on a customer change.
  useEffect(() => {
    hadActiveContributionJobsRef.current = false
  }, [customerId])
  useEffect(() => {
    const hasActiveJob = (contributionJobs ?? []).some(
      (job) => job.status === 'queued' || job.status === 'processing'
    )
    if (hadActiveContributionJobsRef.current && !hasActiveJob) {
      void refetchContributions()
    }
    hadActiveContributionJobsRef.current = hasActiveJob
  }, [contributionJobs, refetchContributions])

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [modelPendingActivationId, setModelPendingActivationId] = useState<
    string | null
  >(null)

  const handleOpenConfirmModal = (modelId: string) => {
    setModelPendingActivationId(modelId)
    setIsConfirmModalOpen(true)
  }

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false)
    setModelPendingActivationId(null)
  }

  const selectedRuleForConfirmation = data?.find(
    (rule) => rule.id === Number(modelPendingActivationId)
  )

  const handleDownloadBenchmarkPlansCSV = async () => {
    if (!selectedBenchmarkSnapshot) return

    try {
      const response = await epsClientV1.get(
        `/benchmark-plans/${selectedBenchmarkSnapshot}/download`,
        { responseType: 'blob' }
      )

      downloadFile(
        response.data,
        `benchmark-plans-${selectedBenchmarkSnapshot}.csv`
      )
    } catch (error) {
      notification.error({
        message: 'Download failed:',
        description: (error as Error).message,
      })
    }
  }

  // Initialize the mutation
  const applyModelMutation = applyContributionModel(customerId)

  const activeModel = data?.find((rule) => rule.isActive)
  const benchmarkSnapshotUsed = benchmarkSnapshots?.find(
    (snapshot) => snapshot.id === activeModel?.benchmarkPlanSnapshotId
  )

  const handleModalConfirmation = async () => {
    if (!modelPendingActivationId) return

    try {
      // Call the API to apply the contribution model
      const jobId = await applyModelMutation.mutateAsync({
        customerId,
        modelId: Number(modelPendingActivationId),
        benchmarkId: selectedBenchmarkSnapshot
          ? String(selectedBenchmarkSnapshot)
          : undefined,
      })

      // Store the job ID to trigger polling (jobId can be string or null)
      if (jobId) {
        setManuallyTriggeredJobId(jobId)
      }

      handleCloseConfirmModal()
    } catch (error) {
      notification.error({
        message: 'Failed to apply contribution model:',
        description: (error as Error).message,
      })
      // TODO: Add error handling/notification
    }
  }

  return (
    <>
      <style>
        {`
          @keyframes progress-pulse {
            0%, 100% {  opacity: 0.3; }
            50% {  opacity: 1; }
          }
        `}
      </style>
      <Stack space={20}>
        <ContentSection
          title="Contributions"
          actions={
            <Stack direction="row" align="center" space={20}>
              <Select
                options={yearOptions}
                value={selectedYear}
                onChange={onChangeYear}
              />

              <Upload
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                customRequest={onUpload}
                showUploadList={false}
              >
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  loading={isUploading}
                >
                  Upload Contribution
                </Button>
              </Upload>

              <Button
                icon={<SettingOutlined />}
                onClick={() => router.push(benchmarkPlansPath())}
              >
                Manage Benchmark Plans
              </Button>
            </Stack>
          }
        >
          {isLoading ? (
            <Stack
              direction="row"
              justify="center"
              align="center"
              style={{ height: '100%' }}
            >
              <Spin />
            </Stack>
          ) : data && data.length > 0 ? (
            <>
              <Stack direction="col" space={20}>
                <Stack direction="row" space={20} align="stretch">
                  <Stack direction="col" space={20} style={{ flex: 1 }}>
                    <div
                      style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: 8,
                        padding: '24px',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                        height: '100%',
                      }}
                    >
                      <Stack style={{ marginBottom: 14 }}>
                        <Typography>
                          1 - Select Benchmark Plans Source
                        </Typography>
                        <Select
                          options={benchmarkSnapshotOptions}
                          value={selectedBenchmarkSnapshot}
                          onChange={onChangeBenchmarkSnapshot}
                          loading={isBenchmarkSnapshotsLoading}
                        />
                        {selectedBenchmarkSnapshot && (
                          <Stack direction="row" align="center" space={5}>
                            <Button
                              type="link"
                              style={{ padding: 0 }}
                              onClick={handleDownloadBenchmarkPlansCSV}
                            >
                              Download Benchmark Plans
                            </Button>
                          </Stack>
                        )}
                      </Stack>
                      <Stack>
                        <Typography>2 - Select a Model to Apply</Typography>
                        <Select
                          options={data.map((rule) => ({
                            value: rule.id,
                            label: (
                              <Typography size={14}>
                                {rule.filename}{' '}
                                {convertToLocalFromUTCISODate(rule.uploadedAt)}
                                {rule.isActive && (
                                  <Tag
                                    icon={<CheckCircleOutlined />}
                                    color="#98ce24"
                                    style={{ marginLeft: 10 }}
                                  >
                                    Active
                                  </Tag>
                                )}
                              </Typography>
                            ),
                            isActive: rule.isActive,
                          }))}
                          value={selectedContributionRule?.id}
                          onChange={(value) =>
                            setSelectedContributionRule(
                              data.find((rule) => rule.id === value)
                            )
                          }
                        />
                      </Stack>
                    </div>
                  </Stack>
                  <Stack space={20} style={{ flex: 1 }}>
                    <CurrentActiveModel
                      activeModel={activeModel}
                      benchmarkSnapshot={benchmarkSnapshotUsed}
                    />
                  </Stack>
                </Stack>

                <Grid<any>
                  wrapper={{
                    style: { height: '300px' },
                  }}
                  columnDefs={[
                    {
                      headerName: 'Relationship',
                      field: 'Relationship',
                      pinned: 'left',
                    },
                    {
                      headerName: 'Contribution Amount',
                      field: 'ContributionAmountOrPercentage',
                      pinned: 'left',
                    },
                    {
                      headerName: 'Price Metric',
                      field: 'PriceMetric',
                    },
                    {
                      headerName: 'Metal Type',
                      field: 'MetalType',
                    },
                    {
                      headerName: 'Min Age',
                      field: 'MinAge',
                    },
                    {
                      headerName: 'Max Age',
                      field: 'MaxAge',
                    },
                    {
                      headerName: 'State',
                      field: 'State',
                    },
                    {
                      headerName: 'RA',
                      field: 'RatingArea',
                    },
                    {
                      headerName: 'Employment',
                      field: 'EmploymentStatus',
                    },
                    {
                      headerName: 'Exempt',
                      field: 'ExemptStatus',
                    },
                    {
                      headerName: 'Contribution Group',
                      field: 'ContributionGroup',
                    },
                    {
                      headerName: 'Plan Year',
                      field: 'PlanYear',
                    },
                    {
                      headerName: 'Benefit Type',
                      field: 'BenefitType',
                    },
                    {
                      headerName: 'Contribution Type',
                      field: 'ContributionType',
                    },
                  ]}
                  rowData={selectedContributionRule?.rows ?? []}
                />
              </Stack>
            </>
          ) : (
            <Stack
              direction="row"
              justify="center"
              align="center"
              style={{ height: '100%' }}
            >
              <Typography size={14} color="#003146">
                You haven't uploaded any contributions yet
              </Typography>
            </Stack>
          )}
        </ContentSection>

        <ContentSection
          title="Apply Contribution"
          actions={
            <Stack
              direction="row"
              align="center"
              justify="flex-end"
              space={10}
              style={{ marginTop: 20 }}
            >
              <Button
                variant="solid"
                color="primary"
                disabled={!selectedContributionRule || isProcessing}
                onClick={() => {
                  if (!selectedContributionRule) return

                  handleOpenConfirmModal(selectedContributionRule.id)
                }}
              >
                Apply Contribution
              </Button>
            </Stack>
          }
        >
          {isProcessing && jobResponse && (
            <ContributionProcessingSection
              jobResponse={jobResponse}
              modelLabel={
                contributionJobs?.find(
                  (j) => j.jobId === jobResponse.jobTracking?.jobId
                )?.modelName ??
                data?.find(
                  (rule) =>
                    String(rule.id) === String(jobResponse.job?.data?.modelId)
                )?.filename ??
                'N/A'
              }
              benchmarkLabel={
                contributionJobs?.find(
                  (j) => j.jobId === jobResponse.jobTracking?.jobId
                )?.benchmarkSnapshotName ??
                getBenchmarkLabel(
                  benchmarkSnapshotOptions,
                  String(jobResponse.job?.data?.benchmarkSnapshotId ?? '')
                ) ??
                'N/A'
              }
            />
          )}
          <Grid<any>
            wrapper={{ style: { height: 'auto', width: '100%' } }}
            domLayout="autoHeight"
            rowData={
              isLoadingContributionJobs ? undefined : (contributionJobs ?? [])
            }
            pagination
            paginationPageSize={5}
            paginationPageSizeSelector={[5, 10, 20, 50, 100]}
            noRowsOverlayComponent={GridNoResultsOverlay}
            defaultColDef={{ sortable: true, resizable: true, filter: false }}
            columnDefs={[
              {
                headerName: 'ID',
                field: 'jobId',
                width: 70,
                sortable: false,
                cellRenderer: ({
                  value,
                }: ICellRendererParams<ContributionJobHistoryItem>) => {
                  if (!value) {
                    return null
                  }

                  return (
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(value)
                        Modal.success({
                          title: 'Copied!',
                          content: `Job ID ${value} copied to clipboard`,
                          okText: 'OK',
                        })
                      }}
                      title="Copy job ID"
                    />
                  )
                },
              },
              {
                headerName: 'Model',
                field: 'modelName',
                flex: 1,
                minWidth: 140,
                valueFormatter: ({ value }: ValueFormatterParams) =>
                  value ?? 'N/A',
              },
              {
                headerName: 'Year',
                field: 'year',
                width: 90,
                valueFormatter: ({ value }: ValueFormatterParams) =>
                  value != null ? String(value) : 'N/A',
              },
              {
                headerName: 'Benchmark Snapshot',
                field: 'benchmarkSnapshotName',
                flex: 1,
                minWidth: 160,
                valueFormatter: ({ value }: ValueFormatterParams) =>
                  value ?? 'Live pricing',
              },
              {
                headerName: 'Created At',
                field: 'createdOn',
                width: 200,
                valueFormatter: ({ value }: ValueFormatterParams) =>
                  value
                    ? dayjs.utc(value).local().format('MM/DD/YYYY HH:mm:ss A')
                    : 'N/A',
              },
              {
                headerName: 'Completed At',
                field: 'completedOn',
                width: 200,
                valueFormatter: ({ value }: ValueFormatterParams) =>
                  value
                    ? dayjs.utc(value).local().format('MM/DD/YYYY HH:mm:ss A')
                    : '—',
              },
              {
                headerName: 'Applied By',
                field: 'appliedBy',
                flex: 1,
                minWidth: 140,
              },
              {
                headerName: 'Status',
                field: 'status',
                width: 140,
                cellRenderer: ({
                  data,
                }: ICellRendererParams<ContributionJobHistoryItem>) =>
                  data
                    ? getHistoryStatusLabel(data.status, data.error?.message)
                    : null,
              },
            ]}
          />
        </ContentSection>
        <ContentSection title="Preview">
          <Stack direction="row" space={20}>
            <SearchableByExample
              id={customerId}
              modelId={selectedContributionRule?.id}
              modelName={selectedContributionRule?.filename}
              benchmarkSnapshotId={selectedBenchmarkSnapshot}
              benchmarkSnapshotName={getBenchmarkLabel(
                benchmarkSnapshotOptions,
                String(selectedBenchmarkSnapshot)
              )}
            />
          </Stack>
        </ContentSection>
        <ContributionReportSection
          clientPublicKey={customerId}
          yearOptions={yearOptions}
          selectedYear={selectedYear}
        />
      </Stack>

      {/* confirmation modal for making the model active */}
      <Modal
        open={isConfirmModalOpen}
        onCancel={handleCloseConfirmModal}
        footer={null}
        width={480}
        centered
      >
        <Stack space={20}>
          <Stack space={8}>
            <Typography size={18} bold>
              Confirm Activation
            </Typography>
            <Typography size={14} color="#595959">
              Review the details before making this model active.
            </Typography>
          </Stack>

          <Stack space={12}>
            <Stack space={4}>
              <Typography size={12} color="#8c8c8c">
                Year
              </Typography>
              <Typography size={14}>{selectedYear}</Typography>
            </Stack>

            <Stack space={4}>
              <Typography size={12} color="#8c8c8c">
                Benchmark Snapshot
              </Typography>
              <Typography size={14}>
                {getBenchmarkLabel(
                  benchmarkSnapshotOptions,
                  String(selectedBenchmarkSnapshot)
                )}
              </Typography>
            </Stack>

            <Stack space={4}>
              <Typography size={12} color="#8c8c8c">
                Model
              </Typography>
              <Typography size={14}>
                {selectedRuleForConfirmation?.filename ?? 'N/A'}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" justify="end" space={10}>
            <Button
              onClick={handleCloseConfirmModal}
              variant="outlined"
              disabled={isProcessing || applyModelMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              disabled={!modelPendingActivationId}
              loading={isProcessing || applyModelMutation.isPending}
              onClick={handleModalConfirmation}
            >
              Confirm
            </Button>
          </Stack>
        </Stack>
      </Modal>
    </>
  )
}
